/**
 * Regional Settings Tab Component
 *
 * CR-6256: the group defaults lead the page, and each site below is a binary
 * choice — inherit those defaults, or override them. Only an overriding site
 * shows currency, timezone and date format dropdowns, so the page states the
 * inheritance relationship instead of repeating every field for every site.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';
import { SiteRegionalSettings, PreferencesChoices, GroupRegionalDefaults, GroupRegionalDefaultsUpdate } from './types';
// CR-4707: tenant-level unit-price decimal precision control sits inside
// the Regional tab next to the per-site overrides since both are
// "number-formatting" concerns.
import { UnitPriceDecimalsCard } from './UnitPriceDecimalsCard';
import { GroupDefaultsCard } from './GroupDefaultsCard';
import { SiteRegionalRow } from './SiteRegionalRow';

interface RegionalTabProps {
  siteSettings: SiteRegionalSettings[];
  choices: PreferencesChoices | null;
  groupDefaults: GroupRegionalDefaults | null;
  loadingSites: boolean;
  loadingGroupDefaults: boolean;
  /** Set when the settings fetch failed, so empty states aren't mistaken for "nothing configured". */
  siteSettingsError: string | null;
  savingSite: number | null;
  savingGroupDefaults: boolean;
  togglingInheritance: number | null;
  editedSites: { [key: number]: Partial<SiteRegionalSettings> };
  onSiteSettingChange: (siteId: number, field: string, value: string | boolean | null) => void;
  onSaveSiteSettings: (siteId: number) => void;
  onSiteInheritanceChange: (siteId: number, usesGroupDefaults: boolean) => void;
  onSaveGroupDefaults: (values: GroupRegionalDefaultsUpdate) => void;
}

export function RegionalTab({
  siteSettings,
  choices,
  groupDefaults,
  loadingSites,
  loadingGroupDefaults,
  siteSettingsError,
  savingSite,
  savingGroupDefaults,
  togglingInheritance,
  editedSites,
  onSiteSettingChange,
  onSaveSiteSettings,
  onSiteInheritanceChange,
  onSaveGroupDefaults,
}: RegionalTabProps) {
  return (
    <div className="space-y-4">
      {/* CR-6256: group defaults are the primary reference point, so they
          lead the page rather than sitting under the site list. */}
      <GroupDefaultsCard
        groupDefaults={groupDefaults}
        choices={choices}
        loading={loadingGroupDefaults}
        saving={savingGroupDefaults}
        error={siteSettingsError}
        onSave={onSaveGroupDefaults}
      />

      {/* Site Regional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Site Regional Settings
          </CardTitle>
          <CardDescription>
            Every site inherits the defaults above. Choose Override Default Settings on a site to
            give it its own currency, timezone and date format, or Inherit from Group Company to
            put it back on the defaults. A site&apos;s timezone always decides what it displays;
            the group clock switch above decides which timezone judges its delivery metrics.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSites ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : siteSettingsError ? (
            <div className="flex items-start justify-center gap-2 py-8 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>{siteSettingsError}</p>
            </div>
          ) : siteSettings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No companies found. Companies need to have regional settings configured.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {siteSettings.map((site) => (
                <SiteRegionalRow
                  key={site.id}
                  site={site}
                  choices={choices}
                  edits={editedSites[site.id]}
                  saving={savingSite === site.id}
                  togglingInheritance={togglingInheritance === site.id}
                  onSettingChange={onSiteSettingChange}
                  onSave={onSaveSiteSettings}
                  onInheritanceChange={onSiteInheritanceChange}
                  groupClockOn={
                    // Only for sites of the group whose defaults are loaded — a
                    // Helix user's list spans every group, and a foreign site's
                    // clock is not this card's to describe.
                    groupDefaults?.group_id != null && site.group_id === groupDefaults.group_id
                      ? (groupDefaults.group_values?.judge_on_group_clock ?? false)
                      : undefined
                  }
                  groupTimezoneDisplay={groupDefaults?.settings?.timezone?.display || 'group timezone'}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CR-4707: PO Line pop-up unit-price decimal precision (company-level).
          Sits last (Aaron, 4 Sep): group defaults, then the sites that inherit
          or override them, then the one setting that is about neither. */}
      <UnitPriceDecimalsCard />
    </div>
  );
}
